import { api } from './client';

/*  Specchio dei contratti dei listini.

    Le due porte da cui un file entra sono qui: `importa` per il caricamento a mano —
    il listino arrivato per posta a metà pomeriggio da usare per un'offerta di domani —
    e `passata` per far guardare la cartella adesso invece di aspettare la notte.

    Il registro è in sola lettura e non è una svista: le righe le scrive l'importazione
    dentro la stessa operazione che raccontano, e un registro che si può correggere da
    un'interfaccia non prova niente.  */

export interface ImportazioneLetta {
  id: string;
  nomeFile: string;
  avviata: string;
  conclusa: string | null;
  /** Completata, FornitoreDaRiconoscere o Fallita. */
  esito: string;
  nomeDistributore: string | null;
  codiceFornitoreMago: string | null;
  righeLette: number;
  righeAccettate: number;
  righeScartate: number;
  offerteCreate: number;
  offerteAggiornate: number;
  offerteChiuse: number;
  messaggio: string | null;
  scarti: string | null;
}

export interface EsitoImportazioneListino {
  id: string;
  nomeFile: string;
  esito: string;
  nomeDistributore: string | null;
  codiceFornitoreMago: string | null;
  righeLette: number;
  righeAccettate: number;
  righeScartate: number;
  offerteCreate: number;
  offerteAggiornate: number;
  offerteChiuse: number;
  messaggio: string | null;
  scarti: string | null;
  riuscita: boolean;
}

export interface CorrispondenzaLetta {
  id: string;
  nomeNelFile: string;
  codiceFornitoreMago: string;
  siglaProduttore: string;
}

export interface CorrispondenzaDaScrivere {
  nomeNelFile: string;
  codiceFornitoreMago: string;
  siglaProduttore: string;
}

/*  La griglia degli sconti concordati.

    È l'accordo commerciale tirato fuori dai file Excel. Finora quel numero stava dentro la
    colonna SCONTO_LISTINO dell'ultimo file arrivato — BRANDONI 50, BARBERI 55, ETA 37 — e
    per sapere quanto sconto avessimo su una marca bisognava aprire un file.  */

export interface ScontoLetto {
  id: string;
  codiceFornitoreMago: string;
  fornitore: string;
  siglaProduttore: string;
  produttore: string | null;
  codiceFamigliaSconto: string | null;
  percentuale: number;
  decorrenza: string | null;
  scadenza: string | null;
  nota: string | null;
  inVigore: boolean;
  /** Su quanti prezzi questa riga mette le mani. */
  articoliInteressati: number;
}

export interface ScontoDaScrivere {
  codiceFornitoreMago: string;
  siglaProduttore: string;
  codiceFamigliaSconto: string | null;
  percentuale: number;
  decorrenza: string | null;
  scadenza: string | null;
  nota: string | null;
}

export interface MarcaDaScontare {
  siglaProduttore: string;
  produttore: string | null;
  articoli: number;
  /** Lo sconto che le offerte portano adesso, quando è uno solo. */
  scontoOggi: number | null;
  giaConcordato: boolean;
}

/*  L'import del foglio di sconti esportato da Hopperix.

    Due chiamate e non una: la prima dice cosa farebbe, la seconda scrive. In mezzo c'è
    una persona che dice di chi è il file — il nome del file è una ragione sociale battuta
    a mano in un altro gestionale, e combacia con l'anagrafica di Mago meno di metà delle
    volte. Il più somigliante a «RUBINETTERIA PAFFONI S.P.A.» è «RUBINETTERIE RITMONIO
    S.R.L», e indovinare lì vorrebbe dire scrivere gli sconti di una marca sotto un altro
    fornitore, senza nessun errore e con un effetto sui prezzi che si scopre mesi dopo.  */

export interface FornitoreProposto {
  codiceMago: string;
  denominazione: string;
  /** Il nome combacia a meno della forma societaria e della punteggiatura. */
  esatto: boolean;
}

export interface ScontoDaImportare {
  siglaProduttore: string;
  produttore: string | null;
  codiceFamigliaSconto: string | null;
  percentuale: number;
  decorrenza: string | null;
  scadenza: string | null;
  rigaDelFoglio: number;
  /** Quanti articoli di quella marca hanno già un'offerta aperta da questo fornitore. */
  articoli: number;
  giaConcordato: boolean;
}

export interface RigaScartataSconti {
  numero: number;
  descrizione: string;
  motivo: string;
}

export interface AnteprimaSconti {
  nomeFile: string;
  nomeNelFile: string;
  fornitori: FornitoreProposto[];
  sconti: ScontoDaImportare[];
  scartate: RigaScartataSconti[];
  righeLette: number;
}

export interface EsitoImportSconti {
  codiceFornitoreMago: string;
  fornitore: string;
  scritti: number;
  /** Accordi che c'erano già o che si sovrappongono: non fanno fallire l'import. */
  saltati: RigaScartataSconti[];
  scartate: RigaScartataSconti[];
}

export const sconti = {
  elenco: () => api.get<ScontoLetto[]>('/listini/sconti'),

  anteprimaHopperix: (file: File, codiceFornitore?: string) =>
    api.carica<AnteprimaSconti>(
      '/listini/sconti/hopperix/anteprima' +
        (codiceFornitore ? `?codiceFornitore=${encodeURIComponent(codiceFornitore)}` : ''),
      file,
    ),

  importaHopperix: (file: File, codiceFornitore: string) =>
    api.carica<EsitoImportSconti>(
      `/listini/sconti/hopperix?codiceFornitore=${encodeURIComponent(codiceFornitore)}`,
      file,
    ),

  marche: (codiceFornitore: string) =>
    api.get<MarcaDaScontare[]>(`/listini/sconti/marche/${encodeURIComponent(codiceFornitore)}`),

  concorda: (sconto: ScontoDaScrivere) => api.post<ScontoLetto>('/listini/sconti', sconto),

  correggi: (id: string, sconto: ScontoDaScrivere) =>
    api.put<ScontoLetto>(`/listini/sconti/${id}`, sconto),

  elimina: (id: string) => api.elimina(`/listini/sconti/${id}`),
};

export const listini = {
  registro: (quante = 100) => api.get<ImportazioneLetta[]>(`/listini/importazioni?quante=${quante}`),

  corrispondenze: () => api.get<CorrispondenzaLetta[]>('/listini/corrispondenze'),

  /*  PUT e non POST: il nome nel file è la chiave, e dichiararlo due volte deve dare lo
      stesso risultato invece di due corrispondenze in conflitto.  */
  impostaCorrispondenza: (corrispondenza: CorrispondenzaDaScrivere) =>
    api.put<CorrispondenzaLetta>('/listini/corrispondenze', corrispondenza),

  importa: (file: File) => api.carica<EsitoImportazioneListino>('/listini/importazioni', file),

  passata: () => api.post<EsitoImportazioneListino[]>('/listini/passata', {}),
};
